import React, { useCallback, useEffect, useState } from 'react';
import {
  Link,
  Paper,
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Map as MapIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { SiteProject, UpdateSiteProjectRequest } from '../../models/SiteProject';
import { SiteProjectService } from '../../services/SiteProjectService';
import usePermissions from '../../hooks/usePermissions';

interface SiteInfoTabProps {
  projectId: number;
  /** 페이지 헤더가 보여주는 캐시된 project — 초기 표시용. 없으면 API 호출로 fetch. */
  cachedProject?: Pick<SiteProject, 'name' | 'name_ko' | 'location' | 'address' | 'description' | 'status' | 'dashboard_url' | 'construction_completed_at' | 'final_completed_at'> | null;
  /** 저장 성공 시 호출 — 페이지 header / 사이드바 등이 새 값을 반영하도록. */
  onUpdated?: (project: SiteProject) => void;
}

interface FormState {
  name: string;
  name_ko: string;
  location: string;
  address: string;
  description: string;
  status: 'active' | 'inactive' | 'maintenance';
  dashboard_url: string;
  construction_completed_at: string; // YYYY-MM-DD
  final_completed_at: string; // YYYY-MM-DD
  // 담당자 정보는 ContactsSection (다인 site_contacts 테이블) 으로 이관 — 이
  // 폼에서는 더 이상 다루지 않는다.
}

const STATUS_OPTIONS: Array<{ value: FormState['status']; label: string; color: 'success' | 'default' | 'warning' }> = [
  { value: 'active', label: '운영중', color: 'success' },
  { value: 'inactive', label: '중지됨', color: 'default' },
  { value: 'maintenance', label: '점검중', color: 'warning' },
];

const toDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toIsoOrNull = (yyyy_mm_dd: string): string | null => {
  if (!yyyy_mm_dd) return null;
  const d = new Date(`${yyyy_mm_dd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const fromProject = (p: SiteProject | NonNullable<SiteInfoTabProps['cachedProject']> | null): FormState => ({
  name: p?.name ?? '',
  name_ko: p?.name_ko ?? '',
  location: p?.location ?? '',
  address: p?.address ?? '',
  description: p?.description ?? '',
  status: ((p?.status as FormState['status']) ?? 'active'),
  dashboard_url: p?.dashboard_url ?? '',
  construction_completed_at: toDateInput((p as SiteProject)?.construction_completed_at),
  final_completed_at: toDateInput((p as SiteProject)?.final_completed_at),
});

/**
 * 현장 설정 페이지의 기본 정보 탭.
 *
 * 기본 상태: 모든 필드 read-only 카드. "수정" 클릭 시 → 인라인 편집 모드(텍스트
 * 필드/셀렉트). 저장 시 PUT /v0.1/site-management/projects/{id} 로 반영.
 *
 * cachedProject 가 있으면 초기에 그걸로 폼을 채우고 화면을 즉시 보여주되,
 * 동시에 API GET 으로 최신 데이터로 동기화한다 (서버가 다른 필드를 갖고
 * 있을 수 있으므로).
 */
const SiteInfoTab: React.FC<SiteInfoTabProps> = ({ projectId, cachedProject, onUpdated }) => {
  const { canEdit } = usePermissions();
  const [project, setProject] = useState<SiteProject | null>(null);
  const [form, setForm] = useState<FormState>(() => fromProject(cachedProject ?? null));
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const refresh = useCallback(async () => {
    if (!Number.isFinite(projectId)) {
      setError('유효하지 않은 프로젝트 ID');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetched = await SiteProjectService.getProject(projectId);
      setProject(fetched);
      setForm(fromProject(fetched));
    } catch (e) {
      setError(e instanceof Error ? e.message : '현장 정보를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleStartEdit = () => {
    setForm(fromProject(project));
    setFormError(null);
    setEditing(true);
  };

  const handleCancel = () => {
    setForm(fromProject(project));
    setFormError(null);
    setEditing(false);
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return '이름을 입력하세요.';
    if (form.dashboard_url && !/^https?:\/\//.test(form.dashboard_url.trim())) {
      return '대시보드 URL 은 http:// 또는 https:// 로 시작해야 합니다.';
    }
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload: UpdateSiteProjectRequest = {
        name: form.name.trim(),
        name_ko: form.name_ko.trim(),
        location: form.location.trim(),
        address: form.address.trim(),
        description: form.description,
        status: form.status,
        dashboard_url: form.dashboard_url.trim(),
        construction_completed_at: toIsoOrNull(form.construction_completed_at),
        final_completed_at: toIsoOrNull(form.final_completed_at),
      };
      const updated = await SiteProjectService.updateProject(projectId, payload);
      setProject(updated);
      setForm(fromProject(updated));
      setEditing(false);
      setSnack({ open: true, message: '현장 정보가 저장되었습니다.', severity: 'success' });

      // localStorage 캐시도 갱신 — 헤더/사이드바가 즉시 새 이름을 보여주도록.
      try {
        const stored = localStorage.getItem('selectedProject');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (String(parsed.id) === String(updated.id)) {
            localStorage.setItem('selectedProject', JSON.stringify({ ...parsed, ...updated }));
          }
        }
      } catch {
        /* ignore localStorage errors */
      }

      onUpdated?.(updated);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !project) {
    return (
      <Paper variant="outlined" sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error && !project) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          현장 기본 정보
        </Typography>
        {project && (
          <Chip
            label={STATUS_OPTIONS.find((s) => s.value === project.status)?.label ?? project.status}
            size="small"
            color={STATUS_OPTIONS.find((s) => s.value === project.status)?.color ?? 'default'}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {editing ? (
          <>
            <Tooltip title="취소">
              <span>
                <IconButton onClick={handleCancel} disabled={saving} aria-label="취소">
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '저장 중...' : '저장'}
            </Button>
          </>
        ) : (
          canEdit && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleStartEdit}>
              수정
            </Button>
          )
        )}
      </Stack>

      {editing ? (
        <Stack spacing={2}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="이름 (영문, 배포에 사용) *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            size="small"
            fullWidth
            helperText="영문 소문자·숫자·하이픈. 배포 식별자로 사용됩니다."
          />
          <TextField
            label="이름 (한글, 표시용)"
            value={form.name_ko}
            onChange={(e) => setForm((f) => ({ ...f, name_ko: e.target.value }))}
            size="small"
            fullWidth
            placeholder="예: 반포 자이"
            helperText="목록·카드에 표시되는 한글 이름. 비우면 영문 이름이 표시됩니다."
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="위치 (표시용)"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="예: 서울 반포"
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="상태"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as FormState['status'] }))}
              size="small"
              sx={{ flex: 1 }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="주소 (지도/길찾기)"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            placeholder="서울특별시 서초구 반포대로 1"
            size="small"
            fullWidth
            helperText="정확한 주소 — 아래 지도와 외부 길찾기 링크에 사용됩니다."
          />
          <TextField
            label="설명"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            multiline
            minRows={2}
            size="small"
            fullWidth
          />
          <TextField
            label="대시보드 URL"
            value={form.dashboard_url}
            onChange={(e) => setForm((f) => ({ ...f, dashboard_url: e.target.value }))}
            placeholder="https://..."
            size="small"
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="준공 날짜"
              type="date"
              value={form.construction_completed_at}
              onChange={(e) => setForm((f) => ({ ...f, construction_completed_at: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="완공 날짜"
              type="date"
              value={form.final_completed_at}
              onChange={(e) => setForm((f) => ({ ...f, final_completed_at: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1 }}
            />
          </Stack>

          {/* 담당자 입력은 별도 ContactsSection (다인 site_contacts) 으로 이관. */}
        </Stack>
      ) : (
        <>
          <InfoRow label="ID" value={project?.id} />
          <InfoRow label="이름 (한글)" value={project?.name_ko || '—'} />
          <InfoRow label="이름 (영문)" value={project?.name} />
          <InfoRow label="위치" value={project?.location} />
          <InfoRow label="주소" value={project?.address} />
          <InfoRow label="설명" value={project?.description} />
          <InfoRow label="대시보드 URL" value={renderUrl(project?.dashboard_url)} />
          <InfoRow label="준공 날짜" value={formatDate(project?.construction_completed_at) ?? undefined} />
          <InfoRow label="완공 날짜" value={formatDate(project?.final_completed_at) ?? undefined} />
          {/* 담당자 정보는 아래 ContactsSection 의 다인 테이블에서 확인. */}
        </>
      )}

      <SiteLocationMap address={editing ? form.address : project?.address} />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 1.2, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" sx={{ width: 160, color: 'text.secondary', fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ flex: 1, wordBreak: 'break-all' }}>
      {value ?? <span style={{ color: '#888' }}>—</span>}
    </Typography>
  </Box>
);

const formatDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const renderUrl = (url?: string): React.ReactNode => {
  if (!url) return undefined;
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {url}
      <OpenInNewIcon fontSize="inherit" />
    </Link>
  );
};

/**
 * 현장 주소를 지도와 외부 길찾기 링크로 보여주는 컴포넌트.
 *
 * 임베드 지도는 Google Maps 사용 — 네이버/카카오 지도는 X-Frame-Options 로
 * iframe 직접 임베드가 차단되기 때문에 외부 링크로만 노출한다.
 */
const SiteLocationMap: React.FC<{ address?: string }> = ({ address }) => {
  const trimmed = (address ?? '').trim();
  if (!trimmed) {
    return (
      <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
          <MapIcon fontSize="small" />
          <Typography variant="caption">
            주소가 등록되지 않아 지도를 표시할 수 없습니다. "수정" 으로 주소를 입력해주세요.
          </Typography>
        </Stack>
      </Box>
    );
  }

  const encoded = encodeURIComponent(trimmed);
  const embedSrc = `https://maps.google.com/maps?q=${encoded}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  const externalLinks = [
    { label: '네이버 지도', href: `https://map.naver.com/p/search/${encoded}` },
    { label: '카카오 지도', href: `https://map.kakao.com/?q=${encoded}` },
    { label: 'Google Maps', href: `https://www.google.com/maps/search/?api=1&query=${encoded}` },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <MapIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={600}>
          현장 위치
        </Typography>
      </Stack>
      <Box
        sx={{
          width: '100%',
          height: 320,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <iframe
          title={`현장 위치 - ${trimmed}`}
          src={embedSrc}
          width="100%"
          height="100%"
          style={{ border: 0, display: 'block' }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
        {externalLinks.map((l) => (
          <Button
            key={l.label}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="inherit" />}
          >
            {l.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
};

export default SiteInfoTab;
